import { cloneElement, isValidElement, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Home, Star } from 'lucide-react';
import {
  AreaShell,
  Avatar,
  BrandMark,
  Wordmark,
  cn,
  useToast,
  type Crumb as UiCrumb,
  type NavItem,
  type ShellArea,
  type ShellPanel,
  type ShellPanelSection,
} from '@castinflow/ui';
import { NotificationBell } from '../framework/notifications/NotificationBell';
import {
  GlobalSearch,
  QuickAdd,
  coreQuickAddHandlers,
  quickAddHandlers,
} from '../framework/shell-actions';
import { useSession } from '../lib/session';
import { clearMetadataMemory, useMetadata } from '../lib/metadata';
import { useScope } from '../lib/scope';
import { ROLE_LABEL, tr } from '../i18n/tr';
import {
  ADMIN_BACK_ITEM,
  ADMIN_HUB_HREF,
  ADMIN_HUB_ITEM,
  ADMIN_ICON,
  filterAreas,
  resolveNavLocation,
  type AreaId,
  type VisibleNav,
} from './navigation';
import { navText } from './navigation-strings';
import { NavPagesProvider, type NavPage } from './nav-context';
import { PageMetaProvider, useCurrentPageMeta } from './page-meta';
import {
  MAX_PINS,
  pinsKey,
  readPanelHiddenPreference,
  readPins,
  writePanelHiddenPreference,
  writePins,
} from './preferences';
import { UserMenu } from './UserMenu';
import { DemoBanner } from './DemoBanner';
import { AccountStatusBanner, SuspendedScreen, useTenantSuspended } from './AccountStatus';
import { SessionPlanProvider, useModuleStates } from '../framework/plan/plan-context';
import { syncUiLanguage } from '../i18n/language';
import { useShellSettings } from './shell-settings';

// Marka bağlantısı koyu ray üzerinde: odak halkası açık teal (≥ 3:1)
const focusLink =
  'rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar';

/** İkonu verilen boyuta getirir (menü tanımındaki 18 px ikonlar rayda 20, konum satırında 15 px çizilir) */
function sized(icon: ReactNode, size: number): ReactNode {
  return isValidElement<{ size?: number }>(icon) ? cloneElement(icon, { size }) : icon;
}

/** Bağlamsal panel: ana alan → bölümleri; Yönetim → kategori içindeyse o kategorinin ayarları, değilse kategoriler */
export function panelFor(
  nav: VisibleNav,
  areaId: AreaId,
  categoryId: string | null,
): ShellPanel | null {
  if (areaId === 'yonetim') {
    const cat = categoryId === null ? undefined : nav.admin.find((c) => c.id === categoryId);
    if (cat !== undefined)
      return {
        key: `yonetim-${cat.id}`,
        areaId,
        title: cat.label,
        icon: cat.icon,
        back: ADMIN_BACK_ITEM,
        sections: [{ id: cat.id, items: cat.items }],
      };
    return {
      key: 'yonetim',
      areaId,
      title: navText.areas.yonetim.label,
      icon: ADMIN_ICON,
      sections: [
        // Merkezin kendisi (arama burada): telefondaki çekmecede de merkeze buradan gidilir
        { id: 'merkez', items: [ADMIN_HUB_ITEM] },
        {
          id: 'kategoriler',
          label: navText.admin.categoriesSection,
          items: nav.admin.map((c) => ({
            id: `kategori-${c.id}`,
            label: c.label,
            icon: c.icon,
            href: c.href,
            testId: c.testId,
          })),
        },
      ],
    };
  }
  const area = nav.areas.find((a) => a.id === areaId);
  if (area === undefined) return null;
  return {
    key: area.id,
    areaId,
    title: area.label,
    icon: area.icon,
    sections: area.sections,
  };
}

/** Oturum açık kullanıcının kabuğu (rota değişiminde odak yönetimi: `RouteFocus`, App düzeyinde). */
export function AppLayout({ children }: { children?: ReactNode }) {
  return (
    <PageMetaProvider>
      {/* M6 WP 6.1: plan kararı (GET /plan) kabuktaki ve sayfalardaki plan kapılarına bağlanır */}
      <SessionPlanProvider>
        <AppLayoutInner>{children ?? <Outlet />}</AppLayoutInner>
      </SessionPlanProvider>
    </PageMetaProvider>
  );
}

function AppLayoutInner({ children }: { children: ReactNode }) {
  const me = useSession((s) => s.me);
  const logout = useSession((s) => s.logout);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const meta = useCurrentPageMeta();
  const toast = useToast();
  // v3.1 B3: herhangi bir istek "çalışma alanı askıda" (423) dönerse kabuk askı ekranına geçer
  const suspendedMessage = useTenantSuspended();
  const [panelHidden, setPanelHidden] = useState<boolean | null>(() => readPanelHiddenPreference());
  const scope = useScope();
  const metadata = useMetadata(scope);
  // v3.9 hızlı erişim — çalışma alanı + kullanıcı başına (hesap değişince yeniden okunur)
  const pinKey = scope === null ? null : pinsKey(scope.tenantId, scope.userId);
  const [pins, setPins] = useState(() => ({ key: pinKey, value: readPins(pinKey) }));
  if (pins.key !== pinKey) setPins({ key: pinKey, value: readPins(pinKey) });
  // WP 2.10: "Hızlı ürün kaydı" (ürün ayarı; ham değer okunur — ürün şemaları ilk pakete girmez)
  const productQuickCreate =
    metadata.data?.modules.products?.settings.extra['quickCreate'] !== false;
  // WP 2.9: çekirdek oluşturma eylemleri + modüllerin kaydettikleri
  const quickAdd = useMemo(
    () => [
      ...coreQuickAddHandlers(
        (href) => {
          navigate(href);
        },
        { productQuickCreate },
      ),
      ...quickAddHandlers(),
    ],
    [navigate, productQuickCreate],
  );

  // M6 WP 6.2: menü görünürlüğü ve arayüz dili (yanıt gelene kadar menü yalnız yetenek süzgeciyle çizilir)
  const shell = useShellSettings();
  const uiLanguage = shell.data?.uiLanguage;
  useEffect(() => {
    // Çalışma alanı dili tarayıcıdaki tercihten farklıysa tercih güncellenir ve sözlük yeniden çözülsün diye sayfa bir
    // kez yeniden yüklenir (i18n/language.ts; tercih yazılamıyorsa yükleme yapılmaz)
    if (uiLanguage !== undefined && syncUiLanguage(uiLanguage)) window.location.reload();
  }, [uiLanguage]);

  const capabilities = me?.capabilities;
  // v4.5 A-0: modül öğeleri yalnız modül `read_only`/`active` iken çizilir (yanıt beklenirken çizilmez)
  const modules = useModuleStates();
  const nav = useMemo(
    () => filterAreas(capabilities ?? [], shell.data, modules),
    [capabilities, shell.data, modules],
  );
  const pages = useMemo<NavPage[]>(
    () =>
      nav.items
        .filter((i) => i.href !== undefined)
        .map((i) => {
          const cat =
            i.categoryId === undefined ? undefined : nav.admin.find((c) => c.id === i.categoryId);
          const area = nav.areas.find((a) => a.id === i.areaId);
          return {
            id: i.id,
            label: i.label,
            href: i.href ?? '/',
            icon: i.icon,
            where:
              cat !== undefined
                ? `${navText.areas.yonetim.label} · ${cat.label}`
                : (area?.label ?? ''),
            ...(cat !== undefined ? { keywords: navText.admin.items[i.id] ?? '' } : {}),
          };
        }),
    [nav],
  );

  if (!me) return null;
  const name = `${me.user.firstName} ${me.user.lastName}`.trim();
  const loc = resolveNavLocation(nav, location.pathname);

  async function onLogout() {
    await logout();
    qc.clear(); // Plan §5: çıkışta bellek önbelleği temizlenir
    clearMetadataMemory();
    navigate('/login');
  }

  if (suspendedMessage !== null)
    return (
      <SuspendedScreen
        message={suspendedMessage}
        onLogout={() => {
          void onLogout();
        }}
      />
    );

  // Hızlı erişim: sabitlenen kimlikler YETKİ süzgecinden geçmiş menüden seçilir (yetkisi kalkan sayfa görünmez)
  const pinned = pins.value
    .map((id) => nav.items.find((i) => i.id === id))
    .filter((i): i is (typeof nav.items)[number] => i !== undefined);
  const quickAccess: ShellPanelSection | null =
    pinned.length === 0
      ? null
      : {
          id: 'hizli-erisim',
          label: navText.quickAccess.title,
          items: pinned.map((i) => ({
            ...i,
            // Aynı sayfa alan listesinde de olabilir: test kimliği ayrışır
            ...(i.testId === undefined ? {} : { testId: `hizli-${i.testId}` }),
          })),
        };
  const pageIds = new Set(nav.items.map((i) => i.id));
  const togglePin = (id: string) => {
    const has = pins.value.includes(id);
    if (!has && pins.value.length >= MAX_PINS) {
      toast({ tone: 'warning', title: navText.quickAccess.limit(MAX_PINS) });
      return;
    }
    const value = has ? pins.value.filter((x) => x !== id) : [...pins.value, id];
    setPins({ key: pinKey, value });
    writePins(pinKey, value);
  };

  const areas: ShellArea[] = nav.areas.map((a) => ({
    id: a.id,
    label: a.label,
    icon: sized(a.icon, 20),
    href: a.href,
    testId: a.testId,
    itemTestIds: a.sections.flatMap((s) => s.items.flatMap((i) => (i.testId ? [i.testId] : []))),
  }));
  const utilityAreas: ShellArea[] = [
    {
      id: 'yonetim',
      label: navText.areas.yonetim.label,
      icon: sized(ADMIN_ICON, 20),
      href: ADMIN_HUB_HREF,
      testId: 'nav-area-yonetim',
      itemTestIds: [
        'nav-admin-hub',
        ...nav.admin.flatMap((c) => [
          c.testId,
          ...c.items.flatMap((i) => (i.testId ? [i.testId] : [])),
        ]),
      ],
    },
  ];
  const activePanel = loc.areaId === null ? null : panelFor(nav, loc.areaId, loc.categoryId);
  // Yönetim merkezi panelindeki kategori bağlantısı, kategorideki sayfaların test kimliklerini taşır (raydaki alan gibi)
  const categoryItems = new Map(
    nav.admin.map((c) => [
      `kategori-${c.id}`,
      c.items.flatMap((i) => (i.testId ? [i.testId] : [])).join(' '),
    ]),
  );
  const isActive = (item: NavItem) => {
    if (item.href === undefined) return false;
    if (loc.activeHref !== null) return item.href === loc.activeHref;
    // Yönetim merkezinin kendisi (geri bağlantısı kategori sayfasında etkin değildir)
    if (location.pathname === ADMIN_HUB_HREF) return item.href === ADMIN_HUB_HREF;
    // Yönetim kategori sayfası: kategori bağlantısı etkin
    return loc.categoryId !== null && item.href === `${ADMIN_HUB_HREF}/${loc.categoryId}`;
  };

  // Konum satırı (v3.9): alan → (Yönetim kategorisi) → sayfa. Sayfanın kendi kırıntılarından alan adını yineleyen düşer
  const areaLabel =
    loc.areaId === null
      ? null
      : loc.areaId === 'yonetim'
        ? navText.areas.yonetim.label
        : (nav.areas.find((a) => a.id === loc.areaId)?.label ?? null);
  const link =
    (to: string) =>
    (className: string, content: ReactNode): ReactNode => (
      <Link to={to} className={className}>
        {content}
      </Link>
    );
  const areaCrumbs: UiCrumb[] = [];
  // Yönetim merkezinin kendisinde alan kırıntısı geçerli sayfadır: bağlantı yok, başlık kırıntısı yinelenmez
  const atHub = location.pathname === ADMIN_HUB_HREF;
  if (loc.areaId !== null && areaLabel !== null) {
    const area = nav.areas.find((a) => a.id === loc.areaId);
    areaCrumbs.push({
      label: areaLabel,
      icon: sized(loc.areaId === 'yonetim' ? ADMIN_ICON : area?.icon, 15),
      iconWithLabel: true,
      ...(atHub
        ? {}
        : { render: link(loc.areaId === 'yonetim' ? ADMIN_HUB_HREF : (area?.href ?? '/')) }),
    });
    const cat =
      loc.categoryId === null ? undefined : nav.admin.find((c) => c.id === loc.categoryId);
    // Kategori kırıntısı: kategori sayfasının kendisinde ve sayfa adı kategori adıyla aynıysa (Plan ve kullanım) yinelenmez
    if (cat !== undefined && location.pathname !== cat.href && meta?.title !== cat.label)
      areaCrumbs.push({ label: cat.label, render: link(cat.href) });
  } else if (location.pathname !== '/') {
    // Alan dışı sayfa (hesap, arama sonuçları, sistem durumu, bulunamadı): konum ev ikonuyla başlar (v3.5 davranışı)
    areaCrumbs.push({ label: tr.nav.overview, icon: <Home size={15} />, render: link('/') });
  }
  const skip = new Set([
    areaLabel,
    tr.nav.administration,
    ...areaCrumbs.map((c) => (typeof c.label === 'string' ? c.label : '')),
  ]);
  const crumbs: UiCrumb[] = meta
    ? [
        ...areaCrumbs,
        ...meta.crumbs
          // Alan/kategori kırıntısı varken bağlantısız kırıntılar eski menü grubunun adıdır (ör. "Performans"): düşer
          .filter((c) => !skip.has(c.label) && (loc.areaId === null || c.to !== undefined))
          .map((c) => {
            const to = c.to;
            return to === undefined ? { label: c.label } : { label: c.label, render: link(to) };
          }),
        ...(atHub || (areaCrumbs.length > 0 && meta.title === areaLabel)
          ? []
          : [{ label: meta.title }]),
      ]
    : [];

  return (
    <NavPagesProvider pages={pages}>
      <AreaShell
        brandCompact={
          <Link to="/" aria-label={`${tr.appName} — ${tr.nav.overview}`} className={focusLink}>
            <BrandMark tone="inverse" size={28} />
          </Link>
        }
        brand={
          <Link
            to="/"
            className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            <Wordmark />
            <span className="sr-only"> — {tr.nav.overview}</span>
          </Link>
        }
        areas={areas}
        utilityAreas={utilityAreas}
        activeAreaId={loc.areaId}
        panel={activePanel}
        panelForArea={(id) =>
          panelFor(nav, id as AreaId, id === loc.areaId ? loc.categoryId : null)
        }
        quickAccess={quickAccess}
        quickAccessHint={
          <p className="flex items-center gap-1.5 px-3 text-xs text-fg-subtle">
            <Star size={13} aria-hidden="true" className="shrink-0" />
            {navText.quickAccess.hint}
          </p>
        }
        isActive={isActive}
        renderLink={({ item, className, active, children: content, onNavigate }) => (
          <NavLink
            to={item.href ?? '/'}
            end={item.href === '/'}
            className={className}
            aria-current={active ? 'page' : undefined}
            data-testid={item.testId}
            data-area-items={categoryItems.get(item.id)}
            onClick={onNavigate}
          >
            {content}
          </NavLink>
        )}
        renderAreaLink={({ area, className, active, children: content, areaItems }) => (
          <Link
            to={area.href}
            className={className}
            aria-current={active ? 'true' : undefined}
            data-testid={area.testId}
            data-area-items={areaItems}
          >
            {content}
          </Link>
        )}
        itemAccessory={(item) => {
          // Yalnız gerçek sayfalar sabitlenir (geri bağlantısı ve kategori bağlantıları değil)
          if (!pageIds.has(item.id)) return null;
          const on = pins.value.includes(item.id);
          return (
            <button
              type="button"
              onClick={() => {
                togglePin(item.id);
              }}
              aria-pressed={on}
              aria-label={
                on ? navText.quickAccess.unpin(item.label) : navText.quickAccess.pin(item.label)
              }
              data-testid={`pin-${item.id}`}
              className={cn(
                'inline-flex h-7 w-7 items-center justify-center rounded-md transition-[opacity,color,background-color,transform] duration-fast',
                'focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus motion-safe:active:scale-90',
                on
                  ? 'text-primary hover:bg-primary-subtle'
                  : 'text-fg-subtle opacity-0 hover:bg-surface-subtle hover:text-fg group-hover/row:opacity-100 [@media(hover:none)]:opacity-100',
              )}
            >
              <Star size={15} aria-hidden="true" fill={on ? 'currentColor' : 'none'} />
            </button>
          );
        }}
        panelFooter={
          // Bilgi kartı: gerçek oturum verisi; işlem yok (hesap işlemleri üst çubuktaki kullanıcı menüsünde)
          // Tema uyarlaması: uzun çalışma alanı adı kesilmez, alt satıra iner (kesik metin okunmuyordu; impeccable text-overflow)
          <div className="flex min-w-0 items-center gap-2.5" data-testid="sidebar-workspace">
            <Avatar name={name} decorative />
            <div className="min-w-0">
              <p className="break-words text-sm font-semibold text-fg">{name}</p>
              <p className="break-words text-xs text-fg-muted">
                <span className="sr-only">{tr.shell.workspace}: </span>
                {me.tenant.name} · {ROLE_LABEL[me.user.role]}
              </p>
            </div>
          </div>
        }
        panelHiddenPreference={panelHidden}
        onPanelHiddenPreferenceChange={(h) => {
          setPanelHidden(h);
          writePanelHiddenPreference(h);
        }}
        crumbs={crumbs}
        // WP 2.11: genel arama gerçek arama ucuna bağlı (+ v3.9 "Sayfaya git"); hızlı ekle gerçek işleyici yoksa hiç
        // render edilmez; bildirim zili tetiklenmiş hatırlatıcılar (TB-7)
        search={<GlobalSearch />}
        actions={
          <>
            <QuickAdd capabilities={me.capabilities} handlers={quickAdd} />
            <NotificationBell />
            <UserMenu
              me={me}
              onLogout={() => {
                void onLogout();
              }}
            />
          </>
        }
      >
        {/* Tema uyarlaması: demo / hesap şeridi sayfa içeriğiyle aynı kapta ve hizada (boşsa kap da çizilmez) */}
        <div className="mx-auto w-full max-w-content px-4 pt-5 empty:hidden sm:px-6">
          {me.tenant.isDemo ? <DemoBanner /> : <AccountStatusBanner />}
        </div>
        {children}
      </AreaShell>
    </NavPagesProvider>
  );
}
